import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ApartmentOutlined } from '@ant-design/icons';
import { Tag, Button, Select, Modal, message } from 'antd';
import { PlusIcon, TrashIcon, EditIcon } from '@/assets/icons';
import { ActionsDropdown } from '@/components/actions-dropdown';
import DataTable from '@/components/data-table';
import { PageHeader } from '@/components/page-header';
import { ROUTES } from '@/constants';
import useQueryParamsContext from '@/contexts/query-params/useQueryParamsContext';
import { useTimeTrackingStore } from '@/store/useTimeTrackingStore';
import { STATUS_COLORS } from '../_shared/constants';
import {
  useTimeTrackingCompanies,
  useCustomersForCompany,
  useDropUnavailableCompany,
  useProjects,
  useCreateProject,
  useUpdateProject,
  useDeleteProject,
} from '../_shared/hooks';
import { formatDuration } from '../_shared/utils';
import ProjectForm from './_components/ProjectForm';
import ProjectStructureModal from './_components/ProjectStructureModal';

export default function ProjectsPage() {
  const { t } = useTranslation(['forms', 'tables', 'menus', 'messages']);
  const navigate = useNavigate();
  const { queryParams, setQueryParams } = useQueryParamsContext();
  const [formOpen, setFormOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [structureOpen, setStructureOpen] = useState(false);

  const { data: companyData, isLoading: companyIsLoading } =
    useTimeTrackingCompanies();
  const { selectedCompany, setSelectedCompany } = useTimeTrackingStore();

  const dropCompany = useCallback(() => {
    setSelectedCompany(null);
    setQueryParams({ company: undefined });
  }, [setSelectedCompany, setQueryParams]);

  useDropUnavailableCompany(companyData, queryParams?.company, dropCompany);

  // Auto-select: single company, or restore from store
  useEffect(() => {
    if (queryParams?.company) return;
    if (companyData?.isSingle) {
      setQueryParams({ company: companyData.options[0].value });
    } else if (selectedCompany) {
      setQueryParams({ company: selectedCompany });
    }
  }, [companyData, queryParams?.company, selectedCompany, setQueryParams]);

  // Sync store when company changes
  useEffect(() => {
    if (queryParams?.company && queryParams.company !== selectedCompany) {
      setSelectedCompany(queryParams.company);
    }
  }, [queryParams?.company, selectedCompany, setSelectedCompany]);

  const hasCompany = !!queryParams?.company;
  const { data: customers = [] } = useCustomersForCompany(queryParams?.company);
  const { data, status } = useProjects(hasCompany ? queryParams : {});
  const createProject = useCreateProject();
  const updateProject = useUpdateProject();
  const deleteProject = useDeleteProject();

  const projects = data?.data || data?.items || [];
  const total = data?.count || projects.length;
  const canManage = data?.canManageProjects ?? false;

  const handleCreate = useCallback(
    async (values) => {
      try {
        await createProject.mutateAsync(values);
        message.success(t('projectCreated', { ns: 'forms' }));
        setFormOpen(false);
      } catch {
        message.error(t('error', { ns: 'messages' }));
      }
    },
    [createProject, t]
  );

  const handleUpdate = useCallback(
    async (values) => {
      try {
        await updateProject.mutateAsync({
          id: editingProject._id,
          data: values,
        });
        message.success(t('projectUpdated', { ns: 'forms' }));
        setEditingProject(null);
      } catch {
        message.error(t('error', { ns: 'messages' }));
      }
    },
    [updateProject, editingProject, t]
  );

  const handleDelete = useCallback(
    (record) => {
      Modal.confirm({
        title: `${t('confirmDeleteProject', { ns: 'forms' })} "${record.name}"?`,
        okText: t('yes', { ns: 'forms' }),
        cancelText: t('no', { ns: 'forms' }),
        okButtonProps: { danger: true, className: 'btn-danger' },
        cancelButtonProps: { className: 'btn-default' },
        onOk: async () => {
          try {
            await deleteProject.mutateAsync(record._id);
            message.success(t('projectDeleted', { ns: 'forms' }));
          } catch {
            message.error(t('error', { ns: 'messages' }));
          }
        },
      });
    },
    [deleteProject, t]
  );

  const handleRowClick = useCallback(
    (record) => {
      navigate(`${ROUTES.admin}/${ROUTES.timeTracking}/view/${record._id}`);
    },
    [navigate]
  );

  const columns = [
    {
      title: t('projectName', { ns: 'forms' }),
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <span className="flex items-center gap-2">
          <span
            className="inline-block h-3 w-3 rounded-full"
            style={{ backgroundColor: record.color }}
          />
          <span
            className="cursor-pointer font-medium hover:text-blue-500"
            onClick={() => handleRowClick(record)}
          >
            {name}
          </span>
        </span>
      ),
    },
    {
      title: t('projectCode', { ns: 'forms' }),
      dataIndex: 'code',
      key: 'code',
      render: (val) => val || '-',
    },
    {
      title: t('client', { ns: 'forms' }),
      dataIndex: ['client', 'name'],
      key: 'client',
      render: (val) => val || '-',
    },
    {
      title: t('status', { ns: 'tables' }),
      dataIndex: 'status',
      key: 'status',
      render: (val) => (
        <Tag color={STATUS_COLORS[val]}>{t(val, { ns: 'forms' })}</Tag>
      ),
    },
    {
      title: t('members', { ns: 'forms' }),
      dataIndex: 'memberCount',
      key: 'memberCount',
      render: (val) => val || 0,
    },
    {
      title: t('tasks', { ns: 'forms' }),
      dataIndex: 'taskCount',
      key: 'taskCount',
      render: (val) => val || 0,
    },
    canManage && {
      title: t('billable', { ns: 'forms' }),
      dataIndex: 'billable',
      key: 'billable',
      render: (val) =>
        val ? t('yes', { ns: 'forms' }) : t('no', { ns: 'forms' }),
    },
    canManage && {
      title: t('hourlyRate', { ns: 'forms' }),
      dataIndex: 'hourlyRate',
      key: 'hourlyRate',
      render: (val, record) =>
        val ? `${val} ${record.currency?.toUpperCase() || ''}` : '-',
    },
    canManage && {
      title: t('budgetHours', { ns: 'forms' }),
      dataIndex: 'budgetHours',
      key: 'budgetHours',
      render: (val) => (val ? formatDuration(val * 60) : '-'),
    },
    canManage && {
      title: t('actions', { ns: 'tables' }),
      fixed: 'right',
      render: (_, record) => (
        <ActionsDropdown>
          {({ onClose }) => (
            <>
              <Button
                type="text"
                className="btn-default w-full justify-start px-2 hover:!text-zinc-700"
                icon={<EditIcon className="w-[14px]" />}
                onClick={() => {
                  setEditingProject(record);
                  onClose();
                }}
              >
                {t('edit', { ns: 'menus' })}
              </Button>
              <div className="mt-1 border-t pt-1">
                <Button
                  type="text"
                  className="btn-default w-full justify-start px-2 text-red-500 hover:!text-zinc-700"
                  icon={<TrashIcon />}
                  onClick={() => {
                    handleDelete(record);
                    onClose();
                  }}
                >
                  {t('remove', { ns: 'menus' })}
                </Button>
              </div>
            </>
          )}
        </ActionsDropdown>
      ),
    },
  ].filter(Boolean);

  // What the header bar holds. With one company and no right to create
  // projects there is nothing in it, and the bar must go with it:
  // `page__header` keeps its `min-h-12` even when empty, which left a blank
  // band above the table.
  const showCompanySelect = !companyData?.isSingle;
  const showNewProject = canManage && hasCompany;
  const hasHeader = showCompanySelect || showNewProject;

  return (
    <>
      {hasHeader && (
        <PageHeader>
          <div className="flex w-full items-center gap-2 overflow-x-auto bg-white p-1 scrollbar-hide">
            {showCompanySelect && (
              <Select
                showSearch
                optionFilterProp="label"
                placeholder={t('choose', { value: t('company') })}
                className="h-[38px] w-full max-w-[394px] rounded outline outline-accent"
                loading={companyIsLoading}
                options={companyData?.options || []}
                value={queryParams?.company || undefined}
                onChange={(value) =>
                  setQueryParams({ company: value, offset: 1, limit: 10 })
                }
              />
            )}
          </div>

          {showNewProject && (
            <div className="flex w-full items-center justify-end gap-2">
              <Button
                type="primary"
                onClick={() => setFormOpen(true)}
                className="btn-primary h-[38px] whitespace-nowrap"
              >
                <PlusIcon />
                <span>{t('newProject', { ns: 'forms' })}</span>
              </Button>
            </div>
          )}
        </PageHeader>
      )}

      {!hasCompany ? (
        <div className="py-12 text-center text-gray-400">
          {t('selectCompany')}
        </div>
      ) : (
        <DataTable
          total={total}
          data={projects}
          title={t('allProjects', { ns: 'forms' })}
          columns={columns}
          offset={queryParams.offset}
          limit={queryParams.limit}
          onChangePagination={setQueryParams}
          className="h-[calc(100svh_-_264px)]"
          status={status}
          headerChildren={
            canManage && (
              <Button
                type="default"
                className="btn-default h-8 !bg-amber-400 !text-black hover:!bg-amber-500 disabled:!bg-amber-200 disabled:!text-zinc-500"
                icon={<ApartmentOutlined />}
                onClick={() => setStructureOpen(true)}
              >
                {t('structure', { ns: 'menus' })}
              </Button>
            )
          }
        />
      )}

      <ProjectForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleCreate}
        customers={customers}
        isLoading={createProject.isPending}
      />

      <ProjectForm
        open={!!editingProject}
        onClose={() => setEditingProject(null)}
        onSubmit={handleUpdate}
        initialValues={editingProject}
        customers={customers}
        isLoading={updateProject.isPending}
      />

      <ProjectStructureModal
        companyId={queryParams?.company}
        open={structureOpen}
        onClose={() => setStructureOpen(false)}
      />
    </>
  );
}
